import { SessionTrendDataDto } from '@repo/shared/dtos/analytics/AnalyticsResponseDto';
import { dateUtils } from './dateUtils';
import { METRICS_CONFIG, CHART_DATA_CONFIG } from '../config';

export interface TrendAnalysis {
  sortedData: SessionTrendDataDto[];
  bestMonth: SessionTrendDataDto | null;
  worstMonth: SessionTrendDataDto | null;
  totalGrowth: number;
  averageGrowth: number;
}

export const chartUtils = {
  analyzeTrends: (data: SessionTrendDataDto[]): TrendAnalysis => {
    if (!data || data.length === 0) {
      return {
        sortedData: [],
        bestMonth: null,
        worstMonth: null,
        totalGrowth: METRICS_CONFIG.DEFAULT_VALUE,
        averageGrowth: METRICS_CONFIG.DEFAULT_VALUE,
      };
    }

    const sortedData = dateUtils.sortChronologically(data);

    const bestMonth = sortedData.reduce((best, current) =>
      current.sessionCount > best.sessionCount ? current : best,
    );

    const worstMonth = sortedData.reduce((worst, current) =>
      current.sessionCount < worst.sessionCount ? current : worst,
    );

    const firstMonth = sortedData[0];
    const lastMonth = sortedData[sortedData.length - 1];

    const totalGrowth =
      firstMonth && lastMonth && firstMonth.sessionCount > METRICS_CONFIG.MIN_VALUE_FOR_GROWTH
        ? ((lastMonth.sessionCount - firstMonth.sessionCount) / firstMonth.sessionCount) * 100
        : METRICS_CONFIG.DEFAULT_VALUE;

    const validChanges = sortedData
      .map(trend => trend.percentageChange)
      .filter((change): change is number => change !== null && change !== undefined);

    const averageGrowth =
      validChanges.length > 0
        ? validChanges.reduce((sum, change) => sum + change, METRICS_CONFIG.DEFAULT_VALUE) /
          validChanges.length
        : METRICS_CONFIG.DEFAULT_VALUE;

    return {
      sortedData,
      bestMonth,
      worstMonth,
      totalGrowth,
      averageGrowth,
    };
  },

  prepareLineChartData: (
    data: SessionTrendDataDto[],
    showMetric: 'sessions' | 'minutes',
  ): Array<{
    id: string;
    color: string;
    data: Array<{ x: string; y: number; fullData: SessionTrendDataDto }>;
  }> => {
    if (!data || data.length === 0) return [];

    const sortedData = dateUtils.sortChronologically(data);

    if (showMetric === 'sessions') {
      return [
        {
          id: 'sessions % change',
          color: CHART_DATA_CONFIG.LINE_COLORS.SESSIONS,
          data: sortedData.map(trend => ({
            x: dateUtils.formatMonth(trend.monthStart),
            y: trend.percentageChange || METRICS_CONFIG.DEFAULT_VALUE,
            fullData: trend,
          })),
        },
      ];
    } else {
      return [
        {
          id: 'minutes % change',
          color: CHART_DATA_CONFIG.LINE_COLORS.MINUTES,
          data: sortedData.map(trend => ({
            x: dateUtils.formatMonth(trend.monthStart),
            y: trend.minutesPercentageChange || METRICS_CONFIG.DEFAULT_VALUE,
            fullData: trend,
          })),
        },
      ];
    }
  },

  formatDuration: (minutes: number): string => {
    const safe = Number.isFinite(minutes) ? Math.max(0, Math.round(minutes)) : 0;
    return `${safe}m`;
  },

  formatNumber: (num: number): string => {
    const value = Number.isFinite(num) ? Math.round(num) : 0;
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
  },

  safeReduce: <T>(
    array: T[],
    reducer: (acc: T, current: T) => T,
    fallback: T | null = null,
  ): T | null => {
    if (!array || array.length === 0) return fallback;
    return array.reduce(reducer);
  },
};
